// 人事同步 modal（accountV2.jsp 工具列「人事同步」→ accountV2/syncPeopleModal.jsp）。
//
// legacy：modal 兩個欄位（基準日 baseDate：readonly datepicker、必填；同步身份證
// syncCountryId：文字）＋「確定」鈕 → `syncTsghPeople()` →
// `config/tsgh/syncPeopleAjax {eDate, syncCountryId}` → info／alert（accountV2.jsp:498-511）。
//
// 雛形：真的同步要打後端，雛形以假資料層呈現流程——「確定」跑 processing 後顯示完成訊息。
// 不呼叫 DataSource，所以**不帶 data-effect**（criteria C5-j：只有真的呼叫 DataSource 的
// 按鈕才可以標）；trigger 鈕帶 data-modal-trigger（D-3）。
//
// 【證據等級】§7 第 3 級（原始碼）。基準日預設值用 fixtures 的固定基準日（2026/01/05），
// 不用 Date.now()——【雛形值待實機校準】。

import { useState, type ReactElement } from 'react';
import { Button } from '@/components/base/Button';
import { TextInput } from '@/components/base/TextInput';
import { ModalFrame, useDialogs } from '@/components/layouts';

/** fixtures 的固定基準日（mocks/fixtures.ts DATE_EPOCH_MS＝2026/01/05）。 */
const DEFAULT_BASE_DATE = '2026/01/05';

export interface SyncPeopleModalProps {
  readonly pageId: string;
  readonly onClose: () => void;
}

export function SyncPeopleModal({ pageId, onClose }: SyncPeopleModalProps): ReactElement {
  const dialogs = useDialogs();
  const [baseDate, setBaseDate] = useState(DEFAULT_BASE_DATE);
  const [syncCountryId, setSyncCountryId] = useState('');
  const [busy, setBusy] = useState(false);

  const submit = () => {
    void (async () => {
      if (baseDate.trim() === '') {
        // accountV2.jsp:496 逐字：showInfoDialog('未輸入基準日!')——legacy 用 info 不是 alert。
        await dialogs.info('未輸入基準日!');
        return;
      }
      setBusy(true);
      try {
        // legacy：POST config/tsgh/syncPeopleAjax（accountV2.jsp:500-503）；雛形不接後端。
        await dialogs.processing.wrap(new Promise<void>((resolve) => {
          setTimeout(resolve, 0);
        }));
        await dialogs.info('人事同步完成');
        onClose();
      } finally {
        setBusy(false);
      }
    })();
  };

  return (
    <ModalFrame
      uiId={`${pageId}-sync-people-dialog`}
      open
      title="人事同步"
      size="md"
      onClose={onClose}
      footer={
        <Button
          uiId={`${pageId}-sync-people-dialog-ok`}
          variant="primary"
          size="sm"
          type="button"
          disabled={busy}
          onClick={submit}
        >
          確定
        </Button>
      }
    >
      <div data-ui-id={`${pageId}-sync-people-form`} className="space-y-3">
        <div className="flex items-center gap-2">
          <label className="w-24 text-sm" htmlFor={`${pageId}-sync-base-date`}>
            <span className="text-danger">*</span>基準日：
          </label>
          <TextInput
            uiId={`${pageId}-sync-base-date`}
            value={baseDate}
            onChange={setBaseDate}
            readOnly={busy}
          />
        </div>
        <div className="flex items-center gap-2">
          <label className="w-24 text-sm" htmlFor={`${pageId}-sync-country-id`}>
            同步身份證：
          </label>
          <TextInput
            uiId={`${pageId}-sync-country-id`}
            value={syncCountryId}
            onChange={setSyncCountryId}
            readOnly={busy}
          />
        </div>
      </div>
    </ModalFrame>
  );
}
